import type { MouseEventHandler, ReactNode } from 'react';

import Icon from 'components/Icon';
import { getInputClassName } from 'components/InputUi';
import LoadingBorder from 'components/LoadingBorder';
import PlainButton from 'components/PlainButton';
import { useDebounce, useId, useRefObject } from 'hooks';
import type { InputHTMLAttributesWithRef } from 'types';
import { cn, cnm } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

interface BaseProps extends InputHTMLAttributesWithRef<HTMLInputElement> {
	className?: string;
	hasSearchIcon?: boolean;
	hasVisibleLabel?: boolean;
	inputClassName?: string;
	inputLabel: ReactNode;
	isLoading?: boolean;
	/** Delay before the loading state is shown, in milliseconds. */
	loadingDelay?: number;
	loadingStyle?: 'button' | 'field';
	onInputClearClick?: MouseEventHandler<HTMLButtonElement>;
}

interface WithSubmitButtonProps extends BaseProps {
	hasSubmitButton?: true;
	submitButtonLabel: string;
}
interface WithoutSubmitButtonProps extends BaseProps {
	hasSubmitButton: false;
	submitButtonLabel?: never;
}

export type Props = WithSubmitButtonProps | WithoutSubmitButtonProps;

/** A search field with clear button, placeholder and button with icon */
export default function SearchField({
	className,
	hasSearchIcon,
	hasSubmitButton = true,
	hasVisibleLabel = false,
	id: idProp,
	inputClassName,
	inputLabel,
	isLoading = false,
	loadingDelay,
	loadingStyle = 'button',
	onInputClearClick,
	ref,
	submitButtonLabel,
	...inputAttrs
}: Props) {
	const { t } = useI18n();
	const inputRef = useRefObject(ref);
	const fallbackId = useId('search-field');
	const id = idProp || fallbackId;
	// Stringify to make zero count.
	const hasClearButton = Boolean(inputAttrs.value?.toString());

	const debouncedIsLoading = useDebounce(
		isLoading,
		// Let the prop affect the start delay. If it's set to 1000ms, going
		// back to 'not loading' shouldn't be delayed for that long.
		isLoading ? loadingDelay : 100,
	);
	const fieldLoadingFadeOutDelay = 200;
	const fieldLoadingTransitionClass = 'duration-200';
	const isFieldLoadingTransitionActive = useDebounce(
		debouncedIsLoading,
		fieldLoadingFadeOutDelay,
	);

	// Check both regular and debounced to make the showing delayed
	// and the hiding immediate.
	const isLoadingButton =
		isLoading && debouncedIsLoading && loadingStyle === 'button';

	return (
		<>
			<label className={cn(!hasVisibleLabel && 'sr-only')} htmlFor={id}>
				{inputLabel}
			</label>
			<div className={cnm('group/search-field relative z-1 w-full', className)}>
				{loadingStyle === 'field' && (
					// Would be more logical to wrap the field instead of placing this
					// behind, but that would make the hover/focus border/outline styles
					// more difficult to achieve.
					<LoadingBorder
						// Match input field.
						borderRadius={4}
						borderColor={
							debouncedIsLoading || isFieldLoadingTransitionActive
								? 'strong'
								: 'transparent'
						}
						trailColor="subtle"
						fadeOutTransitionSpeed={fieldLoadingFadeOutDelay}
						// Only show if the search takes a while.
						isActive={debouncedIsLoading}
						className="absolute -inset-px -z-1"
					/>
				)}
				{hasSearchIcon && (
					<Icon
						name="search"
						className="absolute top-1/2 left-4 -translate-y-1/2"
					/>
				)}
				<input
					{...inputAttrs}
					ref={inputRef}
					type="text"
					id={id}
					autoCapitalize="none"
					autoComplete="off"
					autoCorrect="off"
					enterKeyHint="search"
					inputMode="search"
					className={getInputClassName({
						extra: cn(
							'h-12 w-full',
							'group-hover/search-field:border-focus group-hover/search-field:outline-1 group-hover/search-field:outline-focus group-hover/search-field:outline-solid',
							'bg-clip-padding transition-[border-color,outline-color] placeholder:text-inherit',
							fieldLoadingTransitionClass,
							// Match right padding with button widths below
							hasClearButton && !hasSubmitButton && 'pr-10',
							!hasClearButton && hasSubmitButton && 'pr-12',
							hasClearButton && hasSubmitButton && 'pr-22',
							hasSearchIcon && 'pl-12',
							debouncedIsLoading &&
								loadingStyle === 'field' &&
								'border-transparent! outline-transparent! duration-0',
							inputClassName,
						),
					})}
				/>
				{onInputClearClick && (
					<PlainButton
						className={cn(
							'absolute top-0 bottom-0',
							'flex items-center justify-center',
							// Match width with input right padding above
							'w-10 border-none bg-none hover:opacity-80',
							hasSubmitButton ? 'right-12' : 'right-0',
							!hasClearButton && 'hidden',
						)}
						onClick={(e) => {
							onInputClearClick(e);
							inputRef.current?.focus();
						}}
					>
						<span className="sr-only">{t('general_clear_button_label')}</span>
						<Icon name="circle-cancel" color="base" />
					</PlainButton>
				)}
				{hasSubmitButton && (
					<PlainButton
						type="submit"
						className={cn(
							'absolute top-0 right-0 bottom-0',
							'flex items-center justify-center',
							// Match width with input right padding above
							'w-12 rounded-r-xs border border-brand bg-brand text-inverse hover:border-l-brandHover hover:bg-brandHover',
							'group-hover/search-field:border-y-focus group-hover/search-field:border-r-focus',
							'peer-focus:border-y-focus peer-focus:border-r-focus',
							isLoading && 'border-y-focus border-r-focus',
						)}
					>
						<span className="sr-only">
							{isLoading ? t('general_loading_text') : submitButtonLabel}
						</span>
						{isLoadingButton && <Icon name="loading" />}
						{!isLoadingButton && <Icon name="search" />}
					</PlainButton>
				)}
			</div>
		</>
	);
}
SearchField.displayName = 'SearchField';
